import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { handleErrors } from '../../utils/common';

const STATUS_COLORS = {
    completed: 'success',
    delivered: 'success',
    shipped: 'info',
    processing: 'warning',
    pending: 'warning',
    draft: 'secondary',
    cancelled: 'danger',
};

const FILTER_TABS = [
    { label: 'All', value: '' },
    { label: 'Registered', value: 'false' },
    { label: 'Guest', value: 'true' },
];

const Orders = () => {
    const navigate = useNavigate();
    const limit = 10;

    const [orders, setOrders] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [guestFilter, setGuestFilter] = useState('');

    const fetchOrders = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * limit;
            let url = `/order/list?limit=${limit}&offset=${offset}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (guestFilter !== '') url += `&is_guest=${guestFilter}`;

            const res = await api.get(url);
            if (res.status === 200) {
                setOrders(res.data.orders || []);
                const total = res.data.total || 0;
                setTotalPages(Math.ceil(total / limit) || 1);
            }
        } catch (err) {
            handleErrors(err);
        } finally {
            setLoading(false);
        }
    }, [search, guestFilter]);

    useEffect(() => {
        fetchOrders(currentPage);
    }, [currentPage, fetchOrders]);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
        setCurrentPage(1);
    };

    const handleFilterTab = (value) => {
        setGuestFilter(value);
        setCurrentPage(1);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.ORDERS}>Orders</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <h4 className="card-title mb-0">Orders</h4>
                            <form className="d-flex gap-2" onSubmit={handleSearch}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search order number..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    style={{ width: 220 }}
                                />
                                <button type="submit" className="btn btn-primary btn-sm">
                                    <i className="fas fa-search"></i>
                                </button>
                                {search && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => { setSearch(''); setSearchInput(''); setCurrentPage(1); }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </form>
                        </div>

                        {/* Filter Tabs */}
                        <div className="card-body pb-0">
                            <ul className="nav nav-tabs">
                                {FILTER_TABS.map(tab => (
                                    <li className="nav-item" key={tab.value}>
                                        <button
                                            className={`nav-link ${guestFilter === tab.value ? 'active' : ''}`}
                                            onClick={() => handleFilterTab(tab.value)}
                                        >
                                            {tab.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="card-body">
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="table table-responsive-md">
                                        <thead>
                                            <tr>
                                                <th><strong>#</strong></th>
                                                <th><strong>Order No.</strong></th>
                                                <th><strong>Customer</strong></th>
                                                <th><strong>Date</strong></th>
                                                <th><strong>Status</strong></th>
                                                <th><strong>Payment</strong></th>
                                                <th><strong>Total</strong></th>
                                                <th><strong>Type</strong></th>
                                                <th><strong>Actions</strong></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders && orders.length > 0 ? orders.map((order, index) => (
                                                <tr key={order._id || index}>
                                                    <td><strong>{((currentPage - 1) * limit) + (index + 1)}</strong></td>
                                                    <td><strong>{order.order_number || 'N/A'}</strong></td>
                                                    <td>
                                                        {order.is_guest
                                                            ? (order.first_name ? `${order.first_name} ${order.last_name || ''}`.trim() : 'Guest')
                                                            : (order.user
                                                                ? `${order.user.first_name || ''} ${order.user.last_name || ''}`.trim() || order.user.email || 'N/A'
                                                                : 'N/A')
                                                        }
                                                    </td>
                                                    <td>{formatDate(order.order_date || order.t)}</td>
                                                    <td>
                                                        <span className={`badge badge-${STATUS_COLORS[order.status] || 'secondary'}`}>
                                                            {order.status || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-${order.payment_status === 'paid' ? 'success' : order.payment_status === 'failed' ? 'danger' : 'warning'}`}>
                                                            {order.payment_status || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>AED {(order.total_amount || 0).toFixed(2)}</td>
                                                    <td>
                                                        <span className={`badge badge-${order.is_guest ? 'info' : 'primary'}`}>
                                                            {order.is_guest ? 'Guest' : 'Registered'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-primary shadow btn-xs sharp"
                                                            title="View Detail"
                                                            onClick={() => navigate(`/orders/${order._id}`)}
                                                        >
                                                            <i className="fas fa-eye"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center">No orders found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <div className="card-footer">
                            <ul className="pagination pagination-gutter pagination-primary no-bg">
                                <li className={`page-item page-indicator ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); setCurrentPage(prev => Math.max(prev - 1, 1)); }}>
                                        <i className="la la-angle-left"></i>
                                    </a>
                                </li>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <li className={`page-item ${currentPage === i + 1 ? 'active' : ''}`} key={i}>
                                        <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); setCurrentPage(i + 1); }}>{i + 1}</a>
                                    </li>
                                ))}
                                <li className={`page-item page-indicator ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); setCurrentPage(prev => Math.min(prev + 1, totalPages)); }}>
                                        <i className="la la-angle-right"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Orders;
